import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Ripple } from "primereact/ripple";
import { confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { Dropdown, DropdownChangeEvent, DropdownProps } from 'primereact/dropdown';
import { Button } from "primereact/button";
import NoDataCreated from "../../../../../Components/NoDataCreated";
import DischargeConditionModal from "./DischargeConditionModal";
import DocumentViewModal from "../../../../../Components/DocumentViewModal";
import ErrorPage from "../../../../../Components/ErrorPage";
import PageLoader from "../../../../../Components/PageLoader";
import { goToTop } from "../../../../../Components/goToTop";
import { useAppDispatch, useAppSelector } from "../../../../../Controllers/hooks";
import { fetchAllDischargeCondition } from "../../../../../Controllers/Thunks/DischargeConditionThunks";
import { DischargeConditions, DischargeConditionTypes, Status, statusList } from "../../../../../Controllers/Types/Project/DefaultService/DischargeConditionInterface";
import { dischargeConditionDeleteAPI, dischargeConditionDocumentUploadAPI } from "../../../../../Api/Project/DefaultServices/DischargeConditionApi";
import { setSelectedDischargeCondition } from "../../../../../Controllers/Slices/DischargeConditionSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";
import { checkFileOthers } from "../../../../../Utils/Validator";
import { Dialog } from "primereact/dialog";
import FileInput from "../../../../../Components/FileInput";
import { Tooltip } from "primereact/tooltip";
import { fetchServicesAndDefaulPackages } from "../../../../../Controllers/Slices/projectDetailsSlice";

const DischargeCondition: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    const location = useLocation();

    const projectData = location.state?.data;
    const serviceId = Number(projectData.folder_id);
    // const isShared: boolean = projectData.sharedBy !== null ? true : false;
    const isShared: boolean = projectData.can_edit === false ? true : false;

    const [openMenuId, setOpenMenuId] = useState<number | null>(null);
    const menuRefs = useRef<Record<number, Menu | null>>({});
    const buttonRefs = useRef<Record<number, HTMLButtonElement | null>>({});

    const dischargeConditions = useAppSelector((state) => state.dischargeCondition.dischargeConditions);
    const errorMessage = useAppSelector((state) => state.dischargeCondition.error);
    const dataLoading = useAppSelector((state) => state?.dischargeCondition?.loading);
    const [initialDataloading, setInitialDataLoading] = useState<boolean>(true);

    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');

    const [showDocumentViewModal, setShowDocumentViewModal] = useState(false);
    const [documentFileUrl, setDocumentFileUrl] = useState<string>('');
    const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
    const [selectedDisConId, setSelectedDisConId] = useState<number | null>(null);
    const [selectedDisConData, setSelectedDisConData] = useState<DischargeConditions | null>(null);

    const [filteredDischargeConditionData, setFilteredDischargeConditionData] = useState<DischargeConditions[]>([]);
    const [statusFilter, setStatusFilter] = useState<Status | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

    const dischargeConditionTypeOptions: DischargeConditionTypes[] = [
        { type: 'All' },
        { type: 'Informative' },
        { type: 'Pre-commesment' },
        { type: 'Pre-occupation' }
    ];
    const [dischargeConditionTypesData, setDischargeConditionTypesData] = useState<DischargeConditionTypes[]>(dischargeConditionTypeOptions);
    const [selectedDisConType, setSelectedDisConType] = useState<string>('All');

    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) => hasPermission(state, screen, permission));
    };

    const isPermission_add_discharge_condition = useCanEdit('Discharge Condition', 'add_discharge_condition');
    const isPermission_edit_discharge_condition = useCanEdit('Discharge Condition', 'edit_discharge_condition');
    const isPermission_delete_discharge_condition = useCanEdit('Discharge Condition', 'delete_discharge_condition');

    const [dischargeConditionDocument, setDischargeConditionDocument] = useState<string>('');
    const [showDocumentUploadModal, setShowDocumentUploadModal] = useState<boolean>(false);
    const [documentFile, setDocumentFile] = useState<File | null>(null);
    const [documentFileName, setDocumentFileName] = useState<string>('');
    const [documentFileError, setDocumentFileError] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [isEditDocument, setIsEditDocument] = useState<boolean>(false);

    useEffect(() => {
        applyFilters();
    }, [selectedDisConType, selectedStatus, dischargeConditions]);

    useEffect(() => {
        dischargeConditionDataGet();

        dispatch(fetchServicesAndDefaulPackages(projectData?.project_id!));
    }, [serviceId, projectData]);

    /* ********************************* get data list ************************************************** */
    const dischargeConditionDataGet = async () => {
        if (serviceId) {
            await dispatch(fetchAllDischargeCondition(serviceId));
            setInitialDataLoading(false);
        }
    }
    /* ********************************* get data list ************************************************** */


    /* *************************** For table action menu ************************** */
    const tableActionMenuItems: MenuItem[] = [
        {
            items: [
                {
                    label: 'View',
                    command: () => {
                        if (selectedDisConId !== null && selectedDisConData) {
                            navigate(`/project/detail/default-service/pre-commencement/discharge-condition/detail`, {
                                state: { projectData: projectData, dischargeConditionData: selectedDisConData },
                            });
                            setOpenMenuId(null);
                        }
                    }
                },

                ...((isPermission_edit_discharge_condition && !isShared)
                    ? [{
                        label: 'Edit',
                        command: () => {
                            if (selectedDisConId !== null && selectedDisConData !== null) {
                                handleUpdateDetails(selectedDisConId, selectedDisConData);
                            }
                        }
                    },]
                    : []
                ),

                ...((isPermission_delete_discharge_condition && !isShared)
                    ? [{
                        label: 'Delete',
                        command: () => {
                            if (selectedDisConId !== null) {
                                handleDeleteData(selectedDisConId!);
                            }
                        }
                    },]
                    : []
                ),
            ]
        }];

    /* *************************************************************************** */

    const handleSelectDisConType = (disConType: string) => {
        setSelectedDisConType(disConType);
    };

    const tableActionMenuToggle = (e: React.MouseEvent<HTMLButtonElement>, disContData: DischargeConditions) => {
        e.stopPropagation();

        setSelectedDisConId(disContData.id);
        setSelectedDisConData(disContData);
        dispatch(setSelectedDischargeCondition(disContData));

        if (openMenuId === disContData?.id) {
            menuRefs.current[disContData?.id]?.hide(e);
            setOpenMenuId(null);
        } else {
            if (openMenuId !== null && menuRefs.current[openMenuId]) {
                menuRefs.current[openMenuId]?.hide(e);
            }
            menuRefs.current[disContData?.id!]?.toggle(e);
            setOpenMenuId(disContData?.id!);
        }
    }


    const applyFilters = () => {
        let filteredData = dischargeConditions;

        if (selectedDisConType !== 'All') {
            filteredData = filteredData.filter(item => item.type === selectedDisConType);
        }

        if (selectedDisConType === 'All') {
            filteredData = filteredData;
        }

        if (selectedStatus) {
            filteredData = filteredData.filter(item => item.status === selectedStatus);
        }

        setFilteredDischargeConditionData(filteredData);
    };

    const handleStatusChange = (e: DropdownChangeEvent) => {
        const selectedStatus = e.value as Status;
        setSelectedStatus(selectedStatus?.name || null);
        setStatusFilter(selectedStatus);
    };


    /* ******************************* For table ******************************* */
    const getRowClassName = () => {
        return 'table_data_row';
    };

    const conNoBody = (rowData: DischargeConditions) => {
        const status = rowData.status;
        let statusColor = '';

        if (status === 'Approved') {
            statusColor = '#7FF2B3';
        } else if (status === 'Application submitted') {
            statusColor = '#FBFF00';
        } else if (status === 'Pending') {
            statusColor = '#FFC300';
        } else if (status === 'Informative') {
            statusColor = '#DDDDDD';
        }

        return (
            <React.Fragment>
                <Tooltip target={`.statusInfo_${rowData?.id}`} />

                <div className={`number_container cusror_help statusInfo_${rowData?.id}`}
                    data-pr-tooltip={status}
                    data-pr-position="right"
                    style={{ backgroundColor: statusColor }}>
                    {rowData.condition_no}
                </div>
            </React.Fragment>
        )
    }

    const drawingFileBody = (rowData: DischargeConditions) => {
        return (
            <span className="d-flex justify-content-center">
                {rowData.drawing_file !== null ? (
                    <button className="doc_view_btn p-ripple"
                        onClick={() => handleViewDocument(rowData.drawing_file!)}>
                        <Ripple />
                        <i className="bi bi-file-earmark-pdf"></i>
                    </button>
                ) : (
                    <span className="text-secondary text-center">--</span>
                )}
            </span>
        )
    }

    const tableActionBody = (rowData: DischargeConditions) => {
        return (
            <React.Fragment>
                <Button
                    ref={(el) => (buttonRefs.current[rowData?.id as number] = el as HTMLButtonElement | null)}
                    key={`menu_toggle_button_${rowData?.id}`}
                    id={`menu_toggle_button_${rowData?.id}`}
                    icon="bi bi-three-dots-vertical"
                    className="is_btn action_trigger_btn p-ripple"
                    onClick={(e) => tableActionMenuToggle(e, rowData)}
                    aria-controls={`popup_menu_${rowData?.id}`}
                    aria-haspopup
                />

                <Menu
                    ref={(el) => (menuRefs.current[rowData?.id!] = el)}
                    id={`popup_menu_${rowData?.id}`}
                    key={`popup_menu_${rowData?.id}`}
                    model={tableActionMenuItems}
                    popup
                    appendTo={document.body}
                    className="service_action_menu" />
            </React.Fragment>
        )
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (openMenuId !== null) {
                const menuElement = menuRefs.current[openMenuId]?.getElement();
                const buttonElement = buttonRefs.current[openMenuId];

                if (
                    menuElement &&
                    !menuElement.contains(event.target as Node) &&
                    buttonElement &&
                    !buttonElement.contains(event.target as Node)
                ) {
                    const syntheticEvent = { stopPropagation: () => { }, preventDefault: () => { } } as React.SyntheticEvent;
                    menuRefs.current[openMenuId]?.hide(syntheticEvent);
                    setOpenMenuId(null);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openMenuId]);
    /* *************************************************************************** */


    /* ******************************* For detail ******************************* */
    const handleAddDetails = () => {
        setDataState('Add');
        setShowDetailModal(true);
    }

    const handleUpdateDetails = (disContId: number, disCondData: DischargeConditions) => {
        setDataState('Edit');
        setShowDetailModal(true);
        setSelectedDisConId(disContId);
        setSelectedDisConData(disCondData);
        setOpenMenuId(null);
    }


    /* ****************************************** Delete ********************************* */
    const handleDeleteData = (disConId: number): void => {
        setOpenMenuId(null);
        confirmDialog({
            message: 'Are you sure you want to delete the discharge condition detail?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            accept: () => deleteData(disConId),
            dismissableMask: true,
            draggable: false,
            resizable: false,
        });
    }

    const deleteData = async (disConId: number) => {
        const data = await dischargeConditionDeleteAPI(disConId);

        if (data.statusCode === 200) {
            dischargeConditionDataGet();

            if (toast.current) {
                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: `Discharge condition data deleted successfully.`,
                    life: 3000,
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'Error deleting discharge condition data.',
                    life: 3000
                });
            }
        }
    }
    /* ****************************************** Delete ********************************* */

    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
        setSelectedDisConData(null);
        setSelectedDisConId(null);
    }
    /* *************************************************************************** */


    /* ************************** For view drawing file ************************** */
    const handleViewDocument = (document: string) => {
        setDocumentFileUrl(document);
        setShowDocumentViewModal(true);
        setIsEditDocument(false);
    }

    const handleCloseDocumentViewModal = () => {
        setShowDocumentViewModal(false);
        setDocumentFileUrl('');
    };
    /* *************************************************************************** */


    /* **************************** Common functions ***************************** */
    const statusOptionTemplate = (option: Status) => {
        return (
            <div className="d-flex align-items-center w-100 gap-4 justify-content-between">
                <div>{option.name}</div>
                <div className="color_display" style={{ backgroundColor: option.colorCode }}></div>
            </div>
        );
    };

    const selectedStatusTemplate = (option: Status, props: DropdownProps) => {
        if (option) {
            return (
                <div className="color_display_label" style={{ backgroundColor: option.colorCode }}>
                    <span>{option.name}</span>
                </div>
            );
        }

        return <span>{props.placeholder}</span>;
    };

    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* *************************************************************************** */

    /* For Discharege condition document file */
    const allServices = useAppSelector((state) => state.projectDetails.allServices);

    useEffect(() => {
        if (allServices && allServices?.length > 0) {
            const activeService = allServices?.find(service => service?.folder_id === serviceId);
            const serviceDocumentFile = activeService?.file_path;

            if (serviceDocumentFile && serviceDocumentFile !== null) {
                setDischargeConditionDocument(serviceDocumentFile);
            } else {
                setDischargeConditionDocument('');
            }
        }
    }, [serviceId, allServices])

    const handleCloseDocumentUploadModal = () => {
        setShowDocumentUploadModal(false);
        handleResetDocumentFields();
        setLoading(false);
    }

    const handleResetDocumentFields = () => {
        setDocumentFile(null);
        setDocumentFileName('');
        setDocumentFileError('');
    }

    const handleOpenDocumentUploadModal = () => {
        setShowDocumentUploadModal(true);
        setDataState('Add');
    }

    const handleEditDocument = () => {
        setShowDocumentUploadModal(true);
        setDataState('Edit');
    }

    /* Check validation */
    function checkCredentialForDocumentUpload(): boolean {
        const isDocumentFileValid = checkFileOthers(documentFile!, true, setDocumentFileError);

        return isDocumentFileValid;
    }


    function showAPIErrorDocumentUpload(keyName: string, error: string): void {
        if (keyName === "file_path") {
            setDocumentFileError(error);
        }
    }

    /* Clear validation message */
    useEffect(() => {
        checkFileOthers(documentFile!, false, setDocumentFileError);
    }, [documentFile]);

    const handleDocumentFileUpload = (file: File) => {
        setDocumentFile(file);
    };

    const handleClearDocumentFile = () => {
        setDocumentFile(null);
    };

    const handleUploadDischargeConditionDocument = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentialForDocumentUpload()) {
            setLoading(true);

            const data = await dischargeConditionDocumentUploadAPI(serviceId, documentFile!);

            setLoading(false);

            if (data?.statusCode === 200) {
                setLoading(false);
                handleCloseDocumentUploadModal();

                dispatch(fetchServicesAndDefaulPackages(projectData?.project_id!));

                if (dataState === 'Edit') {
                    handleCloseDocumentViewModal();
                }

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "Document uploaded successfully.",
                        life: 3000,
                    });
                }
            } else if (data.statusCode === 422) {
                setLoading(false);
                Object.entries(data.errors ?? []).map(([key, value]) => (
                    showAPIErrorDocumentUpload(key, value[0])
                ))
            } else {
                setLoading(false);
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'There was an error uploading the document.',
                        life: 3000
                    });
                }
            }
        }
    }

    const documentUploadModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Upload discharge condition document
            </h5>

            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseDocumentUploadModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const documentUploadModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseDocumentUploadModal}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}
                onClick={handleUploadDischargeConditionDocument}
                loading={loading}
                className="custom_btn primary"
                disabled={documentFile === null}
            />
        </div>
    );

    const handleViewDischargeConditionDocument = () => {
        setShowDocumentViewModal(true);
        setDocumentFileUrl(dischargeConditionDocument);
        setIsEditDocument(true);
    }

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {dataLoading && initialDataloading ? (
                    <PageLoader />
                ) : errorMessage ? (
                    <ErrorPage errorMessage={errorMessage} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData.job_number} : {projectData.address}
                                </li>
                                <li className="page_breadcrumb_item has_child">
                                    {projectData.folder}
                                </li>
                                <li className="page_breadcrumb_item active">
                                    {projectData.feature_name}
                                </li>
                            </ul>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>
                        <div className="page_header_area mt-sm-3 mt-3">
                            <div className="contact_tabs">
                                {dischargeConditionTypesData.map((disContype, index) => (
                                    <div key={index} className="contact_tab_item">
                                        <button type="button"
                                            className={`contact_tab_btn ${disContype.type === selectedDisConType ? 'active' : ''} p-ripple`}
                                            onClick={() => (handleSelectDisConType(disContype.type))}>
                                            {disContype.type}
                                            <Ripple />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <div className="page_action_area flex_wraped custom">
                                <Dropdown
                                    id="status"
                                    value={statusFilter}
                                    onChange={handleStatusChange}
                                    options={statusList}
                                    optionLabel="name"
                                    itemTemplate={statusOptionTemplate}
                                    valueTemplate={selectedStatusTemplate}
                                    panelClassName="status_option_panel"
                                    placeholder="Select status"
                                    className="form_dropdown filter_dropdown"
                                    showClear
                                />

                                {dischargeConditionDocument ? (
                                    <React.Fragment>
                                        <Tooltip
                                            target={`.documentView`}
                                        />

                                        <button
                                            className="add_data_link_btn file documentView p-ripple"
                                            onClick={handleViewDischargeConditionDocument}
                                            data-pr-tooltip="View reference document"
                                            data-pr-position="left"
                                        >
                                            <i className="bi bi-file-earmark-pdf"></i>
                                            <Ripple />
                                        </button>
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <Tooltip
                                            target={`.documentUpload`}
                                        />

                                        <button
                                            className={`add_data_link_btn documentUpload ${isShared ? 'file' : ''} p-ripple`}
                                            onClick={handleOpenDocumentUploadModal}
                                            data-pr-tooltip="Upload reference document"
                                            data-pr-position="left"
                                            disabled={isShared}
                                        >
                                            <i className={`bi ${!isShared ? 'bi-upload' : 'bi-file-earmark-minus'} `}></i>
                                            <Ripple />
                                        </button>
                                    </React.Fragment>
                                )}

                                {isPermission_add_discharge_condition && !isShared && (
                                    <button className="add_data_link_btn p-ripple" onClick={handleAddDetails}>
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Add details</span>
                                        <Ripple />
                                    </button>)
                                }
                            </div>
                        </div>

                        <div className="page_main_content_section">
                            {dischargeConditions?.length > 0 ? (
                                <DataTable
                                    value={filteredDischargeConditionData}
                                    paginator
                                    size="small"
                                    rows={10}
                                    emptyMessage="No data available"
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    tableStyle={{ minWidth: '50rem' }}
                                    paginatorDropdownAppendTo={'self'}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                    className="page_table p-0 p-sm-2"
                                    rowHover
                                    scrollable
                                    rowClassName={getRowClassName}
                                    loading={dataLoading}
                                >
                                    <Column
                                        header="Con. No."
                                        headerClassName="text_purple"
                                        body={conNoBody}
                                        style={{ width: '8%' }}
                                        frozen
                                    ></Column>

                                    <Column
                                        header="Condion Type"
                                        headerClassName="text_purple"
                                        field="type"
                                        body={(rowData: DischargeConditions) => (
                                            <span className="text_bold text_no_wrap">
                                                {rowData.type ? rowData.type : '---'}
                                            </span>
                                        )}
                                        style={{ width: '15%' }}
                                    ></Column>

                                    <Column
                                        header="Short Description"
                                        headerClassName="text_purple"
                                        field="short_description"
                                        body={(rowData: DischargeConditions) => (
                                            <span>
                                                {rowData.short_description ? rowData.short_description : '---'}
                                            </span>
                                        )}
                                        style={{ width: '31%' }}
                                    ></Column>

                                    <Column
                                        header="Ref. No."
                                        headerClassName="text_purple"
                                        field="reference_no"
                                        body={(rowData: DischargeConditions) => (
                                            <span className="text_no_wrap">
                                                {rowData.reference_no ? rowData.reference_no : '---'}
                                            </span>
                                        )}
                                        style={{ width: '12%' }}
                                    ></Column>

                                    <Column
                                        header="Validation"
                                        headerClassName="text_purple"
                                        field="validation_date"
                                        body={(rowData) => (
                                            <span className="text_no_wrap">
                                                {rowData.validation_date ? rowData.validation_date : '---'}
                                            </span>
                                        )}
                                        style={{ width: '12%' }}
                                    ></Column>

                                    <Column
                                        header="Decision"
                                        headerClassName="text_purple"
                                        field="decision_date"
                                        body={(rowData) => (
                                            <span className="text_no_wrap">
                                                {rowData.decision_date ? rowData.decision_date : '---'}
                                            </span>
                                        )}
                                        style={{ width: '12%' }}
                                    ></Column>

                                    <Column
                                        header="DWG"
                                        headerClassName="text_purple"
                                        alignHeader={"center"}
                                        body={drawingFileBody}
                                        style={{ width: '5%' }}
                                    ></Column>

                                    <Column
                                        body={tableActionBody}
                                        style={{ width: '5%' }}
                                        frozen
                                        alignFrozen="right"
                                    ></Column>
                                </DataTable>
                            ) : (
                                <NoDataCreated
                                    message={`No ${(projectData.feature_name).toLowerCase()} document added. ${!isShared && isPermission_add_discharge_condition ? 'To add the document click on add button.' : ''}`}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div >


            {/* Discharge Condition Add/Edit Modal */}
            <DischargeConditionModal
                isOpen={showDetailModal}
                dataState={dataState}
                onClose={handleCloseDetailModal}
                onFetchData={dischargeConditionDataGet}
                toast={toast}
                serviceId={serviceId}
                selectedDisConData={selectedDisConData!}
            />


            {/* Document upload modal */}
            <Dialog
                visible={showDocumentUploadModal}
                header={documentUploadModalHeader}
                footer={documentUploadModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseDocumentUploadModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
                        <div className="row">
                            <div className="col-12">
                                <FileInput
                                    id="documentFile"
                                    label="Document"
                                    labelHtmlFor="documentFile"
                                    required={false}
                                    value={documentFileName}
                                    onUpload={handleDocumentFileUpload}
                                    onClear={handleClearDocumentFile}
                                    toast={toast}
                                    error={documentFileError}
                                    containerClassName="mb-sm-0"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>


            {/* Document view modal */}
            <DocumentViewModal
                isOpen={showDocumentViewModal}
                onClose={handleCloseDocumentViewModal}
                fileUrl={documentFileUrl!}
                hasEdit={isEditDocument && !isShared}
                onEdit={handleEditDocument}
            />
        </React.Fragment>
    )
}

export default DischargeCondition;